import cookies from 'js-cookie';
import React, { StrictMode } from 'react';
import ReactDOM from 'react-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import { initEmbedSession } from '@/app/bootstrap/embed-session/embed-session';
import PreferencesProvider from '@/app/providers/preferences-provider/PreferencesProvider';
import ThemeProvider from '@/app/providers/theme-provider/ThemeProvider';
import Router from '@/app/routing/Router';
import type { RouterData, QueryParams } from '@/shared/types/tile-packet.types';
import createStore from '@/store/createStore';

// Embed sessions (/embed/app/:id#token=…) rewrite the URL and wrap
// window.fetch — must happen before readQueryParams() below and before
// any component issues a request. No-op on every other route.
initEmbedSession();

/**
 * Pure-SPA bootstrap. There's no SSR anymore, so we don't hydrate from a
 * server-injected `__INITIAL_DATA__` blob — we build the initial router
 * data + query params client-side from window.location.
 */

function readQueryParams(): QueryParams {
  const out: Record<string, string> = {};
  if (typeof window !== 'undefined') {
    const search = new URLSearchParams(window.location.search);
    search.forEach((value, key) => {
      out[key] = value;
    });
  }
  return out as QueryParams;
}

/**
 * Build a TilePacket session channel id.
 *
 * The SDK requires a channel that is 6–128 chars from `[0-9a-zA-Z]` (see
 * `tile-packet-sdk/src/utils.ts#createChannel`). Passing an empty string
 * crashes `<Main>` at construction.
 *
 * Strategy:
 *   1. If the URL has an `appId`, derive the channel from it. The appId is
 *      a 24-char hex Mongo ObjectId — stable across reloads, so devices
 *      connected via QR stay connected.
 *   2. Otherwise fall back to a 10-char random id for default/anonymous
 *      sessions.
 */
function makeChannel(query: QueryParams): string {
  const appId = (query as Record<string, string | undefined>).appId;
  if (appId && /^[0-9a-zA-Z]{6,128}$/.test(appId)) {
    return appId;
  }
  const VALID = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let id = '';
  for (let i = 0; i < 10; i++) {
    id += VALID[Math.floor(Math.random() * VALID.length)];
  }
  return id;
}

const queryParams = readQueryParams();
const data: RouterData = {
  type: 'success',
  defaults: { name: 'TilePacket', channel: makeChannel(queryParams) },
};

const store = createStore({ splitTestSettings: {} });

function TilePacketApp() {
  return (
    <StrictMode>
      <HelmetProvider>
        <Provider store={store}>
          <PreferencesProvider cookies={cookies} queryParams={queryParams}>
            <ThemeProvider>
              <BrowserRouter>
                <Router data={data} queryParams={queryParams} userAgent={navigator.userAgent} />
              </BrowserRouter>
            </ThemeProvider>
          </PreferencesProvider>
        </Provider>
      </HelmetProvider>
    </StrictMode>
  );
}

const rootEl = document.getElementById('root');
if (rootEl) {
  ReactDOM.render(<TilePacketApp />, rootEl);
}
